import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Check, X, Minus, Camera } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";
import { queueInspection } from "../lib/offline";
import { PageHeader } from "../components/ModulePage";
import { Button } from "../components/ui/button";
import { SignaturePad } from "../components/SignaturePad";
import { PhotoUpload } from "../components/PhotoUpload";

const OPTS = [["pass", Check, "border-emerald-500 bg-emerald-500/20 text-emerald-200"], ["fail", X, "border-red-500 bg-red-500/20 text-red-200"], ["na", Minus, "border-slate-500 bg-slate-600/30 text-slate-200"]];
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const itemLabel = (i) => (typeof i === "string" ? i : i.label);
const limitText = (s) => [s.min != null && `min ${s.min}`, s.max != null && `max ${s.max}`].filter(Boolean).join(" / ");

const Reading = ({ spec, val, setVal, tid }) => {
  const onChange = (e) => {
    const v = e.target.value;
    const n = parseFloat(v);
    const ok = !isNaN(n) && (spec.min == null || n >= spec.min) && (spec.max == null || n <= spec.max);
    setVal({ ...val, reading: v, unit: spec.unit, result: v === "" ? undefined : ok ? "pass" : "fail", note: v !== "" && !ok ? `Reading ${v}${spec.unit} outside limit (${limitText(spec)})` : val.note });
  };
  return (
    <div className="mt-2 flex items-center gap-2">
      <input type="number" inputMode="decimal" step="0.1" className="field h-11 w-32 font-mono" placeholder={spec.unit} value={val.reading ?? ""} onChange={onChange} data-testid={`${tid}-reading`} />
      <span className="text-xs text-slate-400">{spec.unit} - {limitText(spec)}</span>
    </div>
  );
};

export const ItemRow = ({ sec, item, val, setVal, prefix = "check", photoRequired = false }) => {
  const label = itemLabel(item);
  const tid = `${prefix}-${slug(sec)}-${slug(label)}`;
  return (
  <div className="border-b border-slate-800 py-3 last:border-0" data-testid={tid}>
    <div className="flex items-center justify-between gap-3">
      <p className="text-sm text-slate-100">{label}{photoRequired && <Camera className="ml-1.5 inline h-3.5 w-3.5 text-amber-300" data-testid={`${tid}-photo-required-icon`} />}</p>
      <div className="flex shrink-0 gap-1.5">
        {OPTS.map(([k, Icon, on]) => (
          <button key={k} type="button" onClick={() => setVal({ ...val, result: k })} data-testid={`${tid}-${k}`}
            className={`flex h-11 w-12 items-center justify-center rounded-md border text-xs font-semibold uppercase transition-colors ${val.result === k ? on : "border-slate-700 text-slate-500"}`}>
            <Icon className="h-4 w-4" />
          </button>
        ))}
      </div>
    </div>
    {typeof item !== "string" && item.unit && <Reading spec={item} val={val} setVal={setVal} tid={tid} />}
    {val.result === "fail" && (
      <input className="field mt-2 h-10" placeholder="Describe the defect" value={val.note || ""} onChange={(e) => setVal({ ...val, note: e.target.value })} data-testid={`${tid}-note`} />
    )}
    {photoRequired && (
      <div className="mt-2 space-y-1">
        <p className={`text-xs ${val.photos?.length ? "text-emerald-300" : "text-amber-300"}`}>Photo required for this check{val.photos?.length ? " - added" : ""}</p>
        <PhotoUpload value={val.photos || []} onChange={(photos) => setVal({ ...val, photos })} testid={`${tid}-photo`} label="Photo" />
      </div>
    )}
  </div>
  );
};

export default function InspectionRunner() {
  const { template } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const assetId = params.get("asset");
  const [tpl, setTpl] = useState(() => JSON.parse(localStorage.getItem("eps_checklists") || "{}")[template]);
  const [asset, setAsset] = useState(null);
  const [vals, setVals] = useState({});
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [signature, setSignature] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/checklists").then((r) => { localStorage.setItem("eps_checklists", JSON.stringify(r.data)); setTpl(r.data[template]); }).catch(() => {});
    if (assetId) api.get(`/assets/${assetId}`).then((r) => setAsset(r.data)).catch(() => {});
  }, [template, assetId]);

  const items = useMemo(() => (tpl?.sections || []).flatMap((s) => s.items.map((i) => [s.title, itemLabel(i), !!i.photo])), [tpl]);
  if (!tpl) return <p className="text-slate-400" data-testid="inspection-loading">Loading checklist... (open this checklist once while online to use it offline)</p>;

  const key = (s, i) => `${s}|${i}`;
  const answered = items.filter(([s, i]) => vals[key(s, i)]?.result).length;
  const fails = items.filter(([s, i]) => vals[key(s, i)]?.result === "fail").length;
  const photosMissing = items.filter(([s, i, p]) => p && !vals[key(s, i)]?.photos?.length).length;
  const passAll = () => setVals(Object.fromEntries(items.map(([s, i]) => [key(s, i), vals[key(s, i)]?.result ? vals[key(s, i)] : { result: "pass" }])));

  const submit = async () => {
    const payload = {
      template, asset_id: assetId, location, site_id: localStorage.getItem("eps_site"), notes, photos, signature, client_id: crypto.randomUUID(), performed_at: new Date().toISOString(),
      results: items.map(([s, i]) => ({ section: s, item: i, result: vals[key(s, i)].result, note: vals[key(s, i)].note || "", reading: vals[key(s, i)].reading, unit: vals[key(s, i)].unit, photos: vals[key(s, i)].photos || [] })),
    };
    setBusy(true);
    try {
      if (!navigator.onLine) throw Object.assign(new Error("offline"), { offline: true });
      const r = await api.post("/inspections", payload);
      toast.success(r.data.work_order_ref ? `Inspection saved - defects raised as ${r.data.work_order_ref}` : "Inspection saved - all passed");
      nav(-1);
    } catch (e) {
      if (e.offline || !e.response) {
        queueInspection(payload);
        toast.success("Saved offline - will sync when back online");
        nav(-1);
      } else toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl" data-testid="inspection-runner">
      <PageHeader eyebrow={`${tpl.module} checklist - every ${tpl.frequency_days} day(s)`} title={tpl.name} description={asset ? `${asset.name} (${asset.asset_code}) - ${asset.location || ""}` : "Site-level inspection"} />
      <div className="sticky top-16 z-20 -mx-4 mb-4 flex items-center justify-between gap-3 border-b border-slate-800 bg-ink/95 px-4 py-3 backdrop-blur sm:mx-0 sm:rounded-md sm:border">
        <span className="font-mono text-sm" data-testid="inspection-progress">{answered}/{items.length} answered{fails ? <span className="text-red-400"> - {fails} fail</span> : ""}</span>
        <Button variant="outline" size="sm" className="h-10 border-slate-700 bg-transparent" onClick={passAll} data-testid="pass-all-btn">Mark rest as pass</Button>
      </div>
      {!asset && <input className="field mb-4" placeholder="Area / location inspected" value={location} onChange={(e) => setLocation(e.target.value)} data-testid="inspection-location" />}
      <div className="space-y-4">
        {tpl.sections.map((s) => (
          <div key={s.title} className="panel px-4 py-2">
            <h3 className="font-display pt-2 text-lg font-bold uppercase text-rose-300">{s.title}</h3>
            {s.items.map((i) => {
              const k = key(s.title, itemLabel(i));
              return <ItemRow key={k} sec={s.title} item={i} photoRequired={!!i.photo} val={vals[k] || {}} setVal={(v) => setVals({ ...vals, [k]: v })} />;
            })}
          </div>
        ))}
        <div className="panel space-y-4 p-4">
          <p className="eyebrow">Photo evidence</p>
          <PhotoUpload value={photos} onChange={setPhotos} testid="inspection-photos" />
          <textarea className="field h-20 py-2" placeholder="Additional notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="inspection-notes" />
          <p className="eyebrow">Inspector signature *</p>
          <SignaturePad onChange={setSignature} testid="inspection-signature" />
          {fails > 0 && <p className="text-sm text-amber-300">{fails} failed item(s) will automatically raise a {tpl.risk}-risk defect work order and escalation.</p>}
          <Button onClick={submit} disabled={busy || answered < items.length || photosMissing > 0 || !signature} className="h-12 w-full bg-rose-600 text-base hover:bg-rose-700" data-testid="inspection-submit-btn">
            {answered < items.length ? `Answer all items (${items.length - answered} left)` : photosMissing ? `Add required photos (${photosMissing} left)` : !signature ? "Sign to submit" : busy ? "Submitting..." : "Submit inspection"}
          </Button>
        </div>
      </div>
    </div>
  );
}
